import React, { useState, type FormEvent } from "react";
import {
  astryx,
  AuthCard,
  AuthLayout,
  AuthLink,
  AuthSubmitButton,
  AuthTextInput,
  PasswordInput,
} from "@shadowcue/ui";
import useNativeFormSubmit from "../../hooks/useNativeFormSubmit";

interface SignInProps {
  formAction: string;
  authenticityToken: string;
  /** The attempted email, kept when Devise re-renders after a failed sign-in. */
  email: string | null;
  alert: string | null;
  notice: string | null;
  forgotPasswordPath: string;
}

/**
 * The portal sign-in, matching the Electron app's LoginView. The org comes
 * from the subdomain here, so there is no organization field.
 *
 * The form posts natively to Devise rather than over XHR, so Devise keeps
 * owning the redirect, the failure re-render and the flash messages.
 */
const SignIn = (props: SignInProps) => {
  const [email, setEmail] = useState<string>(props.email ?? "");
  const [password, setPassword] = useState<string>("");
  const { isSubmitting, markSubmitting } = useNativeFormSubmit();

  const canSubmit = email.trim() !== "" && password !== "" && !isSubmitting;

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (!canSubmit) {
      event.preventDefault();
      return;
    }
    markSubmitting();
  };

  return (
    <AuthLayout>
      <form method="post" action={props.formAction} onSubmit={onSubmit}>
        <input type="hidden" name="authenticity_token" value={props.authenticityToken} />
        <AuthCard subtitle="Sign in to find the right people.">
          {/* Devise's failures are form-level (bad credentials, locked,
              timed out), not attributable to one field, so a Banner
              rather than per-input status. */}
          {props.alert && <astryx.Banner status="error" title={props.alert} container="card" />}
          {props.notice && (
            <astryx.Banner status="success" title={props.notice} container="card" />
          )}

          <astryx.Stack direction="vertical" gap={3}>
            <AuthTextInput
              type="email"
              label="Email"
              value={email}
              onChange={setEmail}
              isLabelHidden
              placeholder="Email"
              htmlName="user[email]"
              hasAutoFocus
              isRequired
              width="100%"
            />
            <PasswordInput
              label="Password"
              value={password}
              onChange={setPassword}
              placeholder="Password"
              htmlName="user[password]"
            />
          </astryx.Stack>

          <astryx.Stack direction="vertical" gap={3}>
            <AuthSubmitButton
              type="submit"
              label={isSubmitting ? "Signing in…" : "Sign in"}
              variant="primary"
              width="100%"
              isDisabled={!canSubmit}
              isLoading={isSubmitting}
            />
            <astryx.Stack direction="horizontal" justify="center">
              <AuthLink href={props.forgotPasswordPath}>
                Forgot password?
              </AuthLink>
            </astryx.Stack>
          </astryx.Stack>
        </AuthCard>
      </form>
    </AuthLayout>
  );
};

export default SignIn;
